import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import type {
  List,
  Project,
  ProjectMember,
  Ticket,
  UserProfile,
} from '@/lib/types'
import { supabase } from '@/lib/supabase'
import { Avatar } from './Avatar'
import { cn, displayListName, projectDotClass, profileName } from '@/lib/utils'
import {
  ArrowRight,
  Check,
  Inbox,
  Loader2,
  Plus,
  Search,
  UserPlus,
  X,
} from 'lucide-react'

interface Props {
  /** The card being moved. Null = modal closed. */
  ticket: Ticket | null
  /** The PROJECT the card currently lives in. Used to detect the inbox
   *  case (sourceProject.is_inbox) and surface the « affecter aussi tous
   *  les futurs messages de ce contact » checkbox. */
  sourceProject?: Project | null
  /** All projects the current user has access to (owned + shared). */
  projects: Project[]
  /** All lists visible to the user (board-wide). The modal filters
   *  this down to the lists of the selected destination project. */
  lists: List[]
  /** Every project_members row the user can see — used to figure out
   *  which subscribers already have access to the destination project. */
  projectMembers: ProjectMember[]
  /** Subscribers of the card being moved (= rows in ticket_subscribers
   *  for this ticket). Excludes the assignee — that's a separate field. */
  subscriberIds: Set<string>
  /** Lookup for displaying avatars + names. */
  profilesById: Record<string, UserProfile>
  /** The viewer — used to label "Vous" rows and to figure out who owns
   *  what (only owners can invite other people to their project). */
  currentUserId: string
  /** Close the dialog without moving. */
  onClose: () => void
  /** Confirm the move. The parent is in charge of:
   *    1. Adding `inviteUserIds` to project_members of `destProjectId`
   *       (only effective if the viewer owns that project).
   *    2. Updating the ticket's project_id + list_id.
   *    3. Cleaning up ticket_subscribers rows whose user lost access. */
  onConfirm: (params: {
    destProjectId: string
    destListId: string
    inviteUserIds: string[]
    /** When true (and the source project is the owner's Inbox), the
     *  parent additionally upserts a sender_routing row so every future
     *  WhatsApp message from this contact lands directly on the
     *  destination project, AND moves the contact's other inbox cards. */
    routeSender?: boolean
    /** WhatsApp id of the sender to route (and other cards' anchor).
     *  Resolved by the dialog from the card's source messages. */
    senderWaId?: string | null
    /** Display name snapshot — used in toast messages by the parent. */
    senderName?: string | null
  }) => Promise<void>
  /** Inbox-only: create a brand-new channel for this contact and route them
   *  to it in one step (instead of picking an existing project). The parent
   *  creates the project, writes sender_routing, and moves the contact's
   *  cards over. Only offered when the source is the Inbox. */
  onCreateChannel?: (params: {
    name: string
    senderWaId: string | null
    senderName: string | null
  }) => Promise<void>
}

/** Cross-project card move dialog.
 *
 *  Workflow:
 *    1. Pick a destination project (any the viewer has access to, other
 *       than the source).
 *    2. Pick the destination list (= one of that project's lists).
 *    3. The card's members (assignee + subscribers, minus the viewer
 *       themselves) are listed in a table:
 *         - Already a member of the dest project → kept as-is.
 *         - Not a member → optional « inviter » checkbox. Disabled when
 *           the viewer isn't the owner of the dest project (they can't
 *           add people there anyway).
 *    4. Confirm → parent handles invites + ticket update + cleanup. */
export function MoveCardDialog({
  ticket,
  sourceProject,
  projects,
  lists,
  projectMembers,
  subscriberIds,
  profilesById,
  currentUserId,
  onClose,
  onConfirm,
  onCreateChannel,
}: Props) {
  const [destProjectId, setDestProjectId] = useState<string | null>(null)
  const [destListId, setDestListId] = useState<string | null>(null)
  const [inviteSet, setInviteSet] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  // Inbox triage: « existing » picks a project from the grid; « new »
  // spins up a fresh channel for this contact in one step.
  const [assignMode, setAssignMode] = useState<'existing' | 'new'>('existing')
  const [newChannelName, setNewChannelName] = useState('')
  // Live filter for the project picker — essential once you have more
  // than a handful of projects, otherwise the grid bloats the modal.
  const [projectFilter, setProjectFilter] = useState('')
  // Inbox-only: the WhatsApp contact that sent this card's messages and
  // the count of OTHER inbox cards from the same contact still waiting
  // for triage. Surfaced as the « affecter ce contact » checkbox.
  const [inboxSender, setInboxSender] = useState<
    | { waId: string; name: string | null; otherCount: number }
    | null
  >(null)
  const [routeSender, setRouteSender] = useState(true)

  const sourceIsInbox = !!sourceProject?.is_inbox

  // Reset state every time the dialog opens on a fresh card.
  useEffect(() => {
    if (!ticket) return
    setDestProjectId(null)
    setDestListId(null)
    setInviteSet(new Set())
    setBusy(false)
    setProjectFilter('')
    setInboxSender(null)
    setRouteSender(true)
    setAssignMode('existing')
    setNewChannelName('')
  }, [ticket?.id])

  // Resolve the inbox card's WhatsApp sender on open. We need:
  //   1. The most-recent inbound message's from_wa_id (= the contact's
  //      WhatsApp id) and name snapshot.
  //   2. The count of OTHER inbox cards from this same contact (so we
  //      can offer to move them as a batch).
  // Only runs when the source project is flagged as inbox. Best-effort —
  // silently leaves the routing banner hidden on any failure.
  useEffect(() => {
    if (!ticket || !sourceIsInbox) {
      setInboxSender(null)
      return
    }
    let cancelled = false
    const srcIds = ticket.source_message_ids ?? []
    if (srcIds.length === 0) return
    ;(async () => {
      const { data: lastInb } = await supabase
        .from('messages')
        .select('from_wa_id, from_wa_name, received_at')
        .in('id', srcIds)
        .eq('direction', 'inbound')
        .not('from_wa_id', 'is', null)
        .order('received_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (cancelled || !lastInb?.from_wa_id) return
      const waId = String(lastInb.from_wa_id)
      const name = (lastInb.from_wa_name as string | null) ?? null
      // Count other inbox cards from this contact: messages with same
      // from_wa_id → ticket overlap on source_message_ids in the same
      // project, minus the current card.
      const { data: senderMsgs } = await supabase
        .from('messages')
        .select('id')
        .eq('project_id', ticket.project_id)
        .eq('from_wa_id', waId)
        .limit(500)
      const senderMsgIds = (senderMsgs ?? []).map(
        (r) => r.id as string,
      )
      let otherCount = 0
      if (senderMsgIds.length > 0) {
        const { data: senderTickets } = await supabase
          .from('tickets')
          .select('id')
          .eq('project_id', ticket.project_id)
          .eq('archived', false)
          .is('done_at', null)
          .overlaps('source_message_ids', senderMsgIds)
        otherCount = (senderTickets ?? []).filter(
          (t) => t.id !== ticket.id,
        ).length
      }
      if (cancelled) return
      setInboxSender({ waId, name, otherCount })
      // Prefill a sensible channel name (the contact's name) for the
      // « new channel » mode — the user can overwrite it.
      setNewChannelName((cur) => cur || (name?.trim() ?? ''))
    })()
    return () => {
      cancelled = true
    }
  }, [ticket?.id, sourceIsInbox, ticket?.project_id])

  // Escape closes (only when not busy — avoid cancelling mid-RPC).
  useEffect(() => {
    if (!ticket) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [ticket, busy, onClose])

  // Eligible destinations = projects the user has access to, minus
  // archived ones and the source project itself (moving to the same
  // project is what drag-and-drop already does, no need here).
  const targets = useMemo(
    () =>
      ticket
        ? projects.filter((p) => !p.archived && p.id !== ticket.project_id)
        : [],
    [projects, ticket],
  )

  // Apply the live search filter to the projects grid. Case-insensitive
  // substring match on the project name. Empty query = show everything.
  const filteredTargets = useMemo(() => {
    const q = projectFilter.trim().toLowerCase()
    if (!q) return targets
    return targets.filter((p) => p.name.toLowerCase().includes(q))
  }, [targets, projectFilter])

  const destProject = useMemo(
    () => targets.find((p) => p.id === destProjectId) ?? null,
    [targets, destProjectId],
  )
  const destLists = useMemo(
    () =>
      destProject
        ? lists
            .filter((l) => l.project_id === destProject.id)
            .sort((a, b) => a.position - b.position)
        : [],
    [lists, destProject],
  )

  // Auto-pick the intake list (or first one) when the destination
  // project changes, so the user only has to click Confirm in the common
  // case.
  useEffect(() => {
    if (!destProject) {
      setDestListId(null)
      return
    }
    const intake = destLists.find((l) => l.is_intake)
    setDestListId((intake ?? destLists[0])?.id ?? null)
  }, [destProject, destLists])

  // Who's the dest project owner? Only the owner can add project_members,
  // so the invite checkboxes are read-only otherwise.
  const canInvite = destProject?.user_id === currentUserId

  // Members of the card to display — assignee + subscribers, deduped,
  // viewer pushed last (or omitted since "moving themselves" is implicit).
  // We split them into "already has access" / "needs invite" based on
  // membership in the destination project.
  const cardMembers = useMemo(() => {
    if (!ticket) return [] as string[]
    const set = new Set<string>(subscriberIds)
    if (ticket.assignee_id) set.add(ticket.assignee_id)
    set.delete(currentUserId)
    return [...set]
  }, [ticket, subscriberIds, currentUserId])

  const destMemberIds = useMemo(() => {
    if (!destProject) return new Set<string>()
    const s = new Set<string>([destProject.user_id])
    for (const m of projectMembers) {
      if (m.project_id === destProject.id) s.add(m.user_id)
    }
    return s
  }, [destProject, projectMembers])

  const { withAccess, withoutAccess } = useMemo(() => {
    const w: string[] = []
    const wo: string[] = []
    for (const id of cardMembers) {
      if (destMemberIds.has(id)) w.push(id)
      else wo.push(id)
    }
    return { withAccess: w, withoutAccess: wo }
  }, [cardMembers, destMemberIds])

  function toggleInvite(userId: string) {
    setInviteSet((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const creatingChannel = sourceIsInbox && assignMode === 'new'

  async function handleConfirm() {
    if (busy) return
    // ── « New channel » path (inbox only) ────────────────────────────
    if (creatingChannel) {
      const name = newChannelName.trim()
      if (!name || !onCreateChannel) return
      setBusy(true)
      try {
        await onCreateChannel({
          name,
          senderWaId: inboxSender?.waId ?? null,
          senderName: inboxSender?.name ?? null,
        })
        onClose()
      } finally {
        setBusy(false)
      }
      return
    }
    // ── « Existing project » path ─────────────────────────────────────
    if (!ticket || !destProjectId || !destListId) return
    setBusy(true)
    try {
      await onConfirm({
        destProjectId,
        destListId,
        inviteUserIds: Array.from(inviteSet),
        routeSender: inboxSender ? routeSender : false,
        senderWaId: inboxSender?.waId ?? null,
        senderName: inboxSender?.name ?? null,
      })
      onClose()
    } finally {
      setBusy(false)
    }
  }

  if (!ticket) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div className="w-full max-w-lg rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-[var(--color-border-soft)] px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              {sourceIsInbox ? 'Affecter à un canal' : 'Déplacer la carte'}
            </div>
            <div className="mt-0.5 truncate text-[14px] font-semibold text-[var(--color-text-strong)]">
              {ticket.title}
            </div>
          </div>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            disabled={busy}
            className="-mr-1 inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-4">
          {/* Inbox routing banner — only when this card lives in the
              owner's Inbox AND we managed to resolve the WhatsApp
              contact behind it. Default ON: assigning a contact to a
              channel almost always means « and route everything from
              them too », so the user gets it for free. They can untick
              for the rare one-off case (moving just this card without
              committing to a permanent home). */}
          {inboxSender && (
            <section className="rounded-2xl border border-[var(--color-accent-bg)] bg-[var(--color-accent-bg)]/30 p-3.5">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={routeSender}
                  onChange={(e) => setRouteSender(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-accent)]"
                />
                <span className="flex-1 text-[12.5px] leading-relaxed text-[var(--color-text)]">
                  <span className="inline-flex items-center gap-1.5 text-[var(--color-text-strong)] font-semibold">
                    <Inbox className="h-3.5 w-3.5 text-[var(--color-accent)]" />
                    Affecter ce contact au canal choisi
                  </span>
                  <span className="mt-1 block text-[12px] text-[var(--color-text-dim)]">
                    Tous les futurs messages WhatsApp de
                    <span className="mx-1 font-medium text-[var(--color-text)]">
                      {inboxSender.name?.trim() || inboxSender.waId}
                    </span>
                    arriveront automatiquement sur ce canal — plus de
                    triage manuel.
                    {inboxSender.otherCount > 0 && (
                      <>
                        {' '}
                        Les{' '}
                        <span className="font-medium text-[var(--color-text)]">
                          {inboxSender.otherCount} carte
                          {inboxSender.otherCount > 1 ? 's' : ''} encore
                          dans la boîte de réception
                        </span>{' '}
                        de ce contact y seront aussi déplacées.
                      </>
                    )}
                  </span>
                </span>
              </label>
            </section>
          )}

          {/* Inbox triage: assign to an EXISTING channel, or spin up a NEW
              one for this contact in one step. */}
          {sourceIsInbox && onCreateChannel && (
            <div className="flex gap-1 rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/40 p-1">
              {(['existing', 'new'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setAssignMode(m)}
                  disabled={busy}
                  className={cn(
                    'flex-1 rounded-lg px-2 py-1.5 text-[12px] font-medium transition-colors',
                    assignMode === m
                      ? 'bg-[var(--color-surface)] text-[var(--color-text-strong)] shadow-sm'
                      : 'text-[var(--color-text-dim)] hover:text-[var(--color-text)]',
                  )}
                >
                  {m === 'existing' ? 'Canal existant' : 'Nouveau canal'}
                </button>
              ))}
            </div>
          )}

          {/* New-channel form (inbox + « new » mode). */}
          {creatingChannel && (
            <section>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                Nom du nouveau canal
              </label>
              <input
                type="text"
                autoFocus
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void handleConfirm()
                  }
                }}
                placeholder="Ex. : Clinique Pro"
                disabled={busy}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] disabled:opacity-50"
              />
              <p className="mt-2 text-[11.5px] leading-relaxed text-[var(--color-text-dim)]">
                Un nouveau canal sera créé pour
                <span className="mx-1 font-medium text-[var(--color-text)]">
                  {inboxSender?.name?.trim() || inboxSender?.waId || 'ce contact'}
                </span>
                : ses prochains messages y seront routés automatiquement
                {inboxSender && inboxSender.otherCount > 0
                  ? `, et ses ${inboxSender.otherCount + 1} cartes de la boîte de réception y seront déplacées.`
                  : ' et cette carte y sera déplacée.'}
              </p>
            </section>
          )}

          {/* Step 1: project picker — search + scrollable grid so 50
              projects don't blow up the modal height. */}
          {!creatingChannel && (
          <section>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                Canal de destination
              </label>
              <span className="text-[10px] tabular-nums text-[var(--color-text-dim)]">
                {filteredTargets.length === targets.length
                  ? `${targets.length}`
                  : `${filteredTargets.length}/${targets.length}`}
              </span>
            </div>

            {targets.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-3 text-[12px] text-[var(--color-text-dim)]">
                Aucun autre canal disponible. Crée-en un ou rejoins-en un
                pour pouvoir déplacer cette carte.
              </p>
            ) : (
              <>
                {/* Search input, only useful when there's more than a
                    couple of projects. Threshold = 6 (one row of the
                    2-column grid + a hint they should narrow down). */}
                {targets.length > 6 && (
                  <div className="relative mb-2">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-dim)]" />
                    <input
                      type="text"
                      autoFocus
                      value={projectFilter}
                      onChange={(e) => setProjectFilter(e.target.value)}
                      placeholder="Rechercher un canal…"
                      disabled={busy}
                      className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1.5 pl-7 pr-7 text-[12.5px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)] disabled:opacity-50"
                    />
                    {projectFilter && (
                      <button
                        type="button"
                        onClick={() => setProjectFilter('')}
                        aria-label="Effacer la recherche"
                        className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}

                {filteredTargets.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-3 text-[12px] text-[var(--color-text-dim)]">
                    Aucun canal ne correspond à « {projectFilter} ».
                  </p>
                ) : (
                  <div
                    className="grid max-h-60 grid-cols-1 gap-1.5 overflow-y-auto rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/30 p-1.5 sm:grid-cols-2"
                  >
                    {filteredTargets.map((p) => {
                      const active = destProjectId === p.id
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setDestProjectId(p.id)}
                          disabled={busy}
                          className={cn(
                            'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-[12.5px] transition-colors',
                            active
                              ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-text-strong)]'
                              : 'border-[var(--color-border-soft)] bg-[var(--color-surface)] text-[var(--color-text)] hover:border-[var(--color-link)]',
                          )}
                        >
                          <span
                            className={cn(
                              'h-2.5 w-2.5 shrink-0 rounded-full',
                              projectDotClass(p.color, p.id),
                            )}
                          />
                          <span className="min-w-0 flex-1 truncate">
                            {p.name}
                          </span>
                          {p.user_id !== currentUserId && (
                            <span className="shrink-0 rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                              Partagé
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </section>
          )}

          {/* Step 2: list picker (only when a project is chosen) */}
          {destProject && (
            <section>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                Liste de destination
              </label>
              {destLists.length === 0 ? (
                <p className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-2 text-[12px] text-[var(--color-text-dim)]">
                  Ce canal n'a aucune liste — impossible d'y déposer la
                  carte. Ajoute-lui d'abord une liste depuis son tableau.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {destLists.map((l) => {
                    const active = destListId === l.id
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setDestListId(l.id)}
                        disabled={busy}
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors',
                          active
                            ? 'bg-[var(--color-accent)] text-white'
                            : 'bg-[var(--color-surface-2)] text-[var(--color-text)] hover:bg-[var(--color-surface-3)]',
                        )}
                      >
                        {displayListName(l)}
                      </button>
                    )
                  })}
                </div>
              )}
            </section>
          )}

          {/* Step 3: members access table — capped + scrollable so a
              card shared with 30 people doesn't push everything else
              off-screen. */}
          {destProject && cardMembers.length > 0 && (
            <section>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
                  Membres de la carte
                </label>
                <span className="text-[10px] tabular-nums text-[var(--color-text-dim)]">
                  {cardMembers.length}
                </span>
              </div>
              <ul className="max-h-64 divide-y divide-[var(--color-border-soft)] overflow-y-auto overflow-x-hidden rounded-xl border border-[var(--color-border-soft)]">
                {withAccess.map((id) => (
                  <MemberRow
                    key={id}
                    userId={id}
                    profile={profilesById[id]}
                    isYou={id === currentUserId}
                    status="hasAccess"
                  />
                ))}
                {withoutAccess.map((id) => (
                  <MemberRow
                    key={id}
                    userId={id}
                    profile={profilesById[id]}
                    isYou={id === currentUserId}
                    status="needsInvite"
                    canInvite={canInvite}
                    inviteChecked={inviteSet.has(id)}
                    onToggleInvite={() => toggleInvite(id)}
                  />
                ))}
              </ul>
              {withoutAccess.length > 0 && !canInvite && (
                <p className="mt-2 text-[11.5px] leading-relaxed text-[var(--color-text-dim)]">
                  Tu n'es pas propriétaire de
                  <span className="mx-1 font-medium">{destProject.name}</span>
                  donc tu ne peux pas y ajouter de nouveaux membres. Ces
                  personnes seront retirées de la carte après le
                  déplacement.
                </p>
              )}
              {withoutAccess.length > 0 && canInvite && (
                <p className="mt-2 text-[11.5px] leading-relaxed text-[var(--color-text-dim)]">
                  Les personnes non cochées seront simplement retirées de
                  la carte après le déplacement.
                </p>
              )}
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/40 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={
              busy ||
              (creatingChannel
                ? !newChannelName.trim()
                : !destProjectId || !destListId)
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : creatingChannel ? (
              <Plus className="h-3.5 w-3.5" />
            ) : (
              <ArrowRight className="h-3.5 w-3.5" />
            )}
            {creatingChannel
              ? 'Créer et affecter'
              : sourceIsInbox
                ? 'Affecter'
                : 'Déplacer'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function MemberRow({
  userId,
  profile,
  isYou,
  status,
  canInvite,
  inviteChecked,
  onToggleInvite,
}: {
  userId: string
  profile: UserProfile | undefined
  isYou: boolean
  status: 'hasAccess' | 'needsInvite'
  canInvite?: boolean
  inviteChecked?: boolean
  onToggleInvite?: () => void
}) {
  const name = profileName(profile) || 'Sans nom'
  return (
    <li className="flex items-center gap-2.5 bg-[var(--color-surface)] px-3 py-2">
      <Avatar
        profile={profile}
        userId={userId}
        noTitle
        className="h-6 w-6"
        textClassName="text-[9px]"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[12.5px] text-[var(--color-text-strong)]">
            {name}
          </span>
          {isYou && (
            <span className="shrink-0 rounded bg-[var(--color-surface-3)] px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
              Vous
            </span>
          )}
        </div>
        <div className="text-[10.5px] text-[var(--color-text-dim)]">
          {status === 'hasAccess'
            ? 'A accès au canal — conservé sur la carte'
            : "N'a pas accès au canal"}
        </div>
      </div>
      {status === 'hasAccess' ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-[var(--color-accent)]" />
      ) : (
        <label
          className={cn(
            'inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors',
            inviteChecked
              ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
              : 'border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-link)] hover:text-[var(--color-text)]',
            !canInvite && 'cursor-not-allowed opacity-50',
          )}
          title={canInvite ? '' : 'Tu ne peux pas inviter sur ce canal'}
        >
          <input
            type="checkbox"
            disabled={!canInvite}
            checked={inviteChecked ?? false}
            onChange={onToggleInvite}
            className="sr-only"
          />
          <UserPlus className="h-3 w-3" />
          {inviteChecked ? 'Invité' : 'Inviter'}
        </label>
      )}
    </li>
  )
}
